import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { Modal } from 'ui-components/components/modal';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalFrameworkMethodsPropType } from 'ui-components/prop-types/modal-framework-methods';
import { Checkbox } from 'ui-components/components/checkbox';
import { ModalFooterCancelButton } from 'ui-components/components/modal/modal-footer/cancel-button';

import { markTermAccepted } from '../../../services/insurance-terms-service';
import { InsuranceProviders } from '../../../enums/insurance-providers';
import { InsuranceTermsLink } from '../../../insurance/terms-link';
import { EventSourceStrings } from '../../../tracking/tracking-constants';

import style from './insurance-terms-modal.module.css';

const messagesMap = {
  [InsuranceProviders.Shipsurance.insuranceProviderId]: defineMessages({
    headerMessage: {
      defaultMessage: 'Shipsurance Terms and Conditions',
      id: '3sbG19',
      description: 'shipsuranceTermsModal.header',
    },
    checkboxMessage: {
      defaultMessage: 'I have read and agree to the <a>Shipsurance Terms and Conditions</a>.',
      id: 'tC+Nwb',
      description: 'Shipsurance terms disclosure message',
    },
  }),
  [InsuranceProviders.XCover.insuranceProviderId]: defineMessages({
    headerMessage: {
      defaultMessage: 'Total Shipping Protection Terms of Coverage',
      id: 'Dct1fk',
      description: 'Header label for the terms of coverage of the product named "Total Shipping Protection"',
    },
    bodyMessage: {
      defaultMessage:
        'This insurance is administered by Cover Genius Trading Pty. Ltd. and underwritten by Berkley Insurance Company (Singapore Branch).',
      id: 'wuAVBq',
      description:
        'Legalese describing the relationship between an insurance product and the companies "Cover Genius Trading Pty. Ltd" and "Berkley Insurance Company"',
    },
    checkboxMessage: {
      defaultMessage: 'I have read and agree to the <a>Total Shipping Protection terms of coverage</a>.',
      id: 'WKRdyS',
      description: 'Checkbox label for the user accepting the terms of coverage of the product named "Total Shipping Protection"',
    },
  }),
};

export const InsuranceTermsModal = ({ modalFrameworkMethods, onAcceptTerms, insuranceProviderId }) => {
  const dispatch = useDispatch();
  const { closeModal } = modalFrameworkMethods;
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);

  const acceptedTerms = useCallback(() => {
    closeModal(false);
    markTermAccepted(dispatch, insuranceProviderId);
    onAcceptTerms();
  }, [closeModal, dispatch, insuranceProviderId, onAcceptTerms]);

  const messages = messagesMap[insuranceProviderId];

  const label = (
    <FormattedMessage
      {...messages.checkboxMessage}
      values={{
        a: (chunks) => (
          <InsuranceTermsLink insuranceProviderId={insuranceProviderId} source={EventSourceStrings.INSURANCE_TERMS_MODAL}>
            {chunks}
          </InsuranceTermsLink>
        ),
      }}
    />
  );

  return (
    <Modal size='medium'>
      <ModalHeader>
        <FormattedMessage {...messages.headerMessage} />
      </ModalHeader>
      <ModalBody className={style['modal-body']}>
        <div className={style['modal-body-text']}>
          {messages?.bodyMessage && <FormattedMessage {...messages.bodyMessage} />}
          <Checkbox checked={hasAcceptedTerms} className={style['terms-checkbox']} label={label} onChange={setHasAcceptedTerms} />
        </div>
      </ModalBody>
      <ModalFooter>
        <ModalFooterCancelButton />
        <Button disabled={!hasAcceptedTerms} onClick={acceptedTerms} theme='primary'>
          <FormattedMessage defaultMessage='Continue' description='shipsuranceTermsModal.continue' id='xnJ2/G' />
        </Button>
      </ModalFooter>
    </Modal>
  );
};

InsuranceTermsModal.propTypes = {
  modalFrameworkMethods: ModalFrameworkMethodsPropType.isRequired,
  onAcceptTerms: PropTypes.func.isRequired,
  insuranceProviderId: PropTypes.oneOf(Object.keys(messagesMap)).isRequired,
};
